import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import MolstarViewer, {MolstarWrapperProps} from 'molbio/molstar/Viewer';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {CenteredSpinner} from 'ui-library/Spinner';

import {GetScratchFile, GetScratchFileVariables} from './network/__generated/GetScratchFile';
import {GET_SCRATCH_FILE_QUERY} from './network/GetScratchFile';
import ScratchFileChip from './ScratchFileChip';
import ScratchFileCSV from './ScratchFileCSV';
import ScratchFileIFrame from './ScratchFileIFrame';
import ScratchFileImage from './ScratchFileImage';
import ScratchFileRenderer from './ScratchFileRenderer';
import {MAX_FILE_SIZE_FOR_PREVIEW_BYTES} from './ScratchFileSizeLimit';
import {ScratchFileContent} from './ScratchFileTypes';

interface ScratchFilePaneContentProps {
  scratchFile: ScratchFileContent;
}

/** @package */
export const MOLSTAR_MIME_TYPE_TO_FORMAT: Record<string, MolstarWrapperProps['format']> = {
  'chemical/x-pdb': 'pdb',
  'chemical/x-pdbqt': 'pdbqt',
  'chemical/x-mmcif': 'mmcif',
};

export function renderFilePreview(scratchFile: ScratchFileContent): JSX.Element | null {
  if (scratchFile.size != null && scratchFile.size > MAX_FILE_SIZE_FOR_PREVIEW_BYTES) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text="This file is too large to preview."
        supportingInfo="Download the file to view its contents."
      />
    );
  }

  const {mimeType} = scratchFile;
  switch (mimeType) {
    // In the future, we could support `HTML` as a first-class file type, so that embedded HTML
    // previews are supported across the whole platform (i.e., for all scratch file callers). For now, we
    // scope it down to Analyses.
    case 'text/html':
      return (
        <ScratchFileIFrame
          scratchFile={scratchFile}
          externalFileWarning="This block contains a file that is external to Benchling."
          // We allow scripts in the iframe because they are needed to render HTML reports generated by JMP.
          dangerouslyAllowScriptsInIFrame={true}
          // Scratch files do not have their Content-Type metadata set, so they default to
          // application/octet-stream. Therefore, we need to tell the S3 signing endpoint to override the
          // Content-Type header to text/html.
          dangerouslyOverrideHTMLFileContentTypeHeader={true}
        />
      );
    case 'application/json':
    case 'application/yaml':
    case 'text/plain':
      return (
        <ScratchFileRenderer scratchFile={scratchFile} fileType="text" loadContent={true}>
          {(content) => (
            <textarea
              className={css(styles.textPreview)}
              value={content}
              readOnly={true}
              placeholder="Empty file"
            />
          )}
        </ScratchFileRenderer>
      );
    case 'text/markdown':
      return (
        <ScratchFileRenderer scratchFile={scratchFile} fileType="markdown" loadContent={true}>
          {(content) => (
            <div className={css(styles.markdownPreview)}>
              <MarkdownContent content={content} />
            </div>
          )}
        </ScratchFileRenderer>
      );
    case 'text/csv':
    case 'text/tsv':
      return <ScratchFileCSV scratchFile={scratchFile} />;
    case 'image/jpeg':
    case 'image/png':
      return <ScratchFileImage scratchFile={scratchFile} />;
    case 'chemical/x-pdb':
    case 'chemical/x-pdbqt':
    case 'chemical/x-mmcif': {
      const format = MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType];
      return (
        <ScratchFileRenderer scratchFile={scratchFile} fileType={format} loadContent={false}>
          {(signedURL) => (
            <MolstarViewer url={signedURL} format={format} className={css(styles.molstarViewer)} />
          )}
        </ScratchFileRenderer>
      );
    }
    default:
      return null;
  }
}

/**
 * Render `Pane` content for the given scratch file for Analysis use cases
 *
 * As of writing, those use cases are:
 *   - Embedded HTML for HTML reports
 *   - Text files for plain text content (.txt, .rtf files)
 *   - Markdown files for markdown content (.md files)
 *   - CSV files for CSV content (.csv, .tsv files)
 *   - File chip for JMP and JRP files
 *
 * This component is intentionally written to be `File`-agnostic, so that it may share code with other
 * scratch file callers more easily in the future.
 */
export default function ScratchFilePaneContent({scratchFile}: ScratchFilePaneContentProps): JSX.Element {
  const filePreview = renderFilePreview(scratchFile);
  return filePreview ?? <ScratchFileChip scratchFile={scratchFile} />;
}

interface QueryingScratchFilePaneContentProps {
  fileId: string;
}

export function QueryingScratchFilePaneContent({fileId}: QueryingScratchFilePaneContentProps): JSX.Element {
  return (
    <BenchlingQuery<GetScratchFile, GetScratchFileVariables>
      query={GET_SCRATCH_FILE_QUERY}
      variables={{id: fileId}}
      renderLoading={() => <CenteredSpinner />}
    >
      {(data) => {
        if (isInaccessibleInfo(data.scratchFile)) {
          return (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this file." />
          );
        }

        return <ScratchFilePaneContent scratchFile={data.scratchFile} />;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  textPreview: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
  },
  markdownPreview: {
    ...FONT_PROXIMA_13,
  },
  molstarViewer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    // We need a minimum height, because if we ever try to render a molstar viewer with a height of 0, it will throw an error.
    minWidth: 10,
    minHeight: 10,
  },
});
